import { ATRIBUTO_MODO } from './teclado-sistema';

/**
 * Regras do teclado na tela do TOTEM (layouts, detecção e escrita no campo).
 * A parte visual fica em `components/teclado-tela.tsx`; aqui só função pura e
 * manipulação de DOM, para poder testar sem montar componente.
 */

import { proximoCampo } from './foco-formulario';

export type LayoutTeclado = 'ABC' | 'NUMERICO' | 'DECIMAL';
export type CampoEditavel = HTMLInputElement | HTMLTextAreaElement;

/** Tecla de caractere ou tecla de função. `largura` é proporcional (flex). */
export type TeclaTeclado =
  | { tipo: 'char'; valor: string; largura?: number }
  | { tipo: 'shift' | 'backspace' | 'acentos' | 'numerico' | 'abc' | 'ok'; largura?: number };

const linha = (chars: string): TeclaTeclado[] => [...chars].map((valor) => ({ tipo: 'char', valor }));

/** QWERTY pt-BR: `ç` na posição do ABNT2 (depois do `l`), não escondida atrás de acento. */
export const LAYOUT_ABC: readonly (readonly TeclaTeclado[])[] = [
  linha('qwertyuiop'),
  linha('asdfghjklç'),
  [{ tipo: 'shift', largura: 1.5 }, ...linha('zxcvbnm'), { tipo: 'backspace', largura: 1.5 }],
  [
    { tipo: 'acentos', largura: 1.5 },
    { tipo: 'numerico', largura: 1.5 },
    { tipo: 'char', valor: ' ', largura: 4 },
    { tipo: 'char', valor: ',' },
    { tipo: 'char', valor: '.' },
    { tipo: 'ok', largura: 2 },
  ],
];

export const LAYOUT_NUMERICO: readonly (readonly TeclaTeclado[])[] = [
  linha('123'),
  linha('456'),
  linha('789'),
  [{ tipo: 'backspace' }, { tipo: 'char', valor: '0' }, { tipo: 'ok' }],
];

export const LAYOUT_DECIMAL: readonly (readonly TeclaTeclado[])[] = [
  linha('123'),
  linha('456'),
  linha('789'),
  [{ tipo: 'char', valor: ',' }, { tipo: 'char', valor: '0' }, { tipo: 'backspace' }],
  [{ tipo: 'ok', largura: 3 }],
];

/**
 * Gaveta de acentuadas. Escolhida no lugar da tecla morta (`´` + `a`): a tecla
 * morta não dá retorno visual enquanto está pendente e é a maior fonte de
 * "o teclado travou" em totem.
 */
export const ACENTUADAS: readonly string[] = ['á', 'à', 'â', 'ã', 'é', 'ê', 'í', 'ó', 'ô', 'õ', 'ú', 'ü'];

export const LAYOUTS: Record<LayoutTeclado, readonly (readonly TeclaTeclado[])[]> = {
  ABC: LAYOUT_ABC,
  NUMERICO: LAYOUT_NUMERICO,
  DECIMAL: LAYOUT_DECIMAL,
};

/** Tipos de `input` que não recebem texto digitado. */
const TIPOS_NAO_DIGITAVEIS = new Set([
  'hidden', 'checkbox', 'radio', 'range', 'file', 'button', 'submit', 'reset', 'color', 'image',
]);

/**
 * Tipos em que ler/escrever `selectionStart` LANÇA InvalidStateError no Chrome
 * ("does not support selection"). Nenhum campo do totem usa hoje, mas um
 * `type="number"` novo derrubaria o teclado na primeira tecla.
 */
const TIPOS_SEM_SELECAO = new Set(['number', 'email', 'date', 'time', 'datetime-local', 'month', 'week']);

/** Posição do caret, com fallback para o fim quando o tipo não suporta seleção. */
export function selecaoDe(el: CampoEditavel): { ini: number; fim: number } {
  try {
    const ini = el.selectionStart ?? el.value.length;
    return { ini, fim: el.selectionEnd ?? ini };
  } catch {
    return { ini: el.value.length, fim: el.value.length };
  }
}

/**
 * Descobre qual layout o campo focado pede; `null` significa "não abrir".
 *
 * O default é ABC de propósito: nenhum dos campos de texto do totem declara
 * `inputMode` (nome, observação do item, observação do pedido, busca), então
 * tratar "sem inputMode" como indefinido deixaria o teclado fechado justamente
 * onde ele mais falta.
 *
 * `data-teclado="abc|numerico|decimal|off"` no campo ou num ancestral força a
 * escolha — escape hatch para o campo que precisar divergir.
 *
 * O `inputMode` é lido primeiro de `data-teclado-modo`: no aparelho da loja o
 * atributo real vira `none`, para o teclado do SISTEMA não abrir por cima do
 * nosso (ver `teclado-sistema.ts`), e o valor que a tela declarou fica ali.
 */
export function layoutDoCampo(el: Element | null): LayoutTeclado | null {
  if (!el) return null;

  const forcado = el.closest?.('[data-teclado]')?.getAttribute('data-teclado')?.toLowerCase();
  if (forcado === 'off') return null;
  if (forcado === 'abc') return 'ABC';
  if (forcado === 'numerico') return 'NUMERICO';
  if (forcado === 'decimal') return 'DECIMAL';

  if (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA') return null;
  const campo = el as CampoEditavel;
  if (campo.disabled || campo.readOnly) return null;
  if (campo instanceof HTMLInputElement && TIPOS_NAO_DIGITAVEIS.has(campo.type)) return null;

  const tipo = campo instanceof HTMLInputElement ? campo.type : 'textarea';
  const modo = (
    campo.getAttribute(ATRIBUTO_MODO) ||
    campo.inputMode ||
    campo.getAttribute('inputmode') ||
    ''
  ).toLowerCase();
  if (modo === 'decimal') return 'DECIMAL';
  if (modo === 'numeric' || tipo === 'tel' || tipo === 'number') return 'NUMERICO';
  return 'ABC';
}

/**
 * O campo de troco (`lerValorTroco`) recusa separador repetido ou trocado —
 * "50.00," vira `NaN` e o cliente vê "informe um valor" sem entender. Por isso
 * o separador entra uma vez só.
 */
export function teclaBloqueada(valor: string, char: string, layout: LayoutTeclado): boolean {
  if (layout !== 'DECIMAL' || char !== ',') return false;
  return valor.includes(',') || valor.includes('.');
}

/**
 * Estado do campo depois de uma tecla, sem tocar no DOM. `maxLength` ausente
 * no DOM vem como -1; backspace ignora o limite.
 */
export function aplicarTecla(
  valor: string,
  ini: number,
  fim: number,
  tecla: { tipo: 'char'; valor: string } | { tipo: 'backspace' },
  maxLength: number,
): { valor: string; caret: number } {
  if (tecla.tipo === 'backspace') {
    if (fim > ini) return { valor: valor.slice(0, ini) + valor.slice(fim), caret: ini };
    if (ini <= 0) return { valor, caret: 0 };
    return { valor: valor.slice(0, ini - 1) + valor.slice(ini), caret: ini - 1 };
  }

  const espacoLivre = maxLength > 0 ? maxLength - (valor.length - (fim - ini)) : Number.POSITIVE_INFINITY;
  if (espacoLivre <= 0) return { valor, caret: ini };

  const texto = tecla.valor.slice(0, espacoLivre);
  return { valor: valor.slice(0, ini) + texto + valor.slice(fim), caret: ini + texto.length };
}

/**
 * Botão de ação principal do diálogo em que o campo está (o CONTINUAR / Enviar
 * pedido do rodapé). O rodapé se identifica com `data-teclado-acao`, em vez de
 * a gente adivinhar por classe do MUI ou por "último botão do DOM".
 *
 * É o que permite a tecla de avanço do teclado: num totem o cliente não deve
 * precisar sair do teclado e viajar até o rodapé para seguir.
 */
export function acaoPrincipal(el: CampoEditavel): HTMLButtonElement | null {
  const dialogo = el.closest('.MuiDialog-paper, [role="dialog"]');
  if (!dialogo) return null;
  return dialogo.querySelector<HTMLButtonElement>('[data-teclado-acao] button');
}

/**
 * O que a tecla de avanço faz neste campo:
 *  - `linha`   textarea: quebra de linha (Enter ali é quebra, não navegação)
 *  - `proximo` ainda há campo depois no mesmo formulário
 *  - `avancar` é o último campo e o diálogo tem ação principal
 *  - `fechar`  não há para onde ir
 */
export function acaoDoAvanco(el: CampoEditavel): 'linha' | 'proximo' | 'avancar' | 'fechar' {
  if (el instanceof HTMLTextAreaElement) return 'linha';
  if (proximoCampo(el)) return 'proximo';
  return acaoPrincipal(el) ? 'avancar' : 'fechar';
}

/**
 * Garante que o campo está de fato focado antes de ler ou escrever a seleção.
 *
 * Nem sempre está: no diálogo de busca o foco acaba no `div` do modal (o MUI
 * devolve o foco ao Paper), e escrever num campo não focado faz o
 * `setSelectionRange` ser descartado quando ele recebe o foco de volta — o caret
 * volta para 0 e o texto sai embaralhado ("Coca" virava "OcaC").
 */
export function garantirFoco(el: CampoEditavel): void {
  if (document.activeElement !== el) el.focus({ preventScroll: true });
}

/**
 * Escreve no campo de um jeito que o React enxergue.
 *
 * `el.value = x` NÃO serve: o React instala um `_valueTracker` na instância do
 * nó, e escrever por ali atualiza o tracker junto — o React conclui que nada
 * mudou e o `onChange` do TextField nunca dispara. Chamamos o setter do
 * prototype (que o tracker não observa) e emitimos um `input` que borbulha
 * (o `onChange` do React é o `input` do DOM, e o React delega na raiz).
 */
export function escreverNoCampo(el: CampoEditavel, valor: string, caret: number): void {
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  setter?.call(el, valor);
  el.dispatchEvent(new Event('input', { bubbles: true }));

  // Depois do input: o re-render do React pode reposicionar a seleção. O campo
  // pode ter rejeitado o caractere (sanitização própria), então o caret é
  // limitado ao que sobrou de verdade.
  const pos = Math.min(caret, el.value.length);
  if (el instanceof HTMLInputElement && TIPOS_SEM_SELECAO.has(el.type)) return;
  try {
    el.setSelectionRange(pos, pos);
  } catch {
    /* type sem suporte a seleção — caret fica no fim */
  }
}
